import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { companyDetails } from "../data/constant";

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = "website",
  structuredData,
  faqs,
}) => {
  const location = useLocation();
  const baseUrl = "https://panthm.com"; // Canonical domain
  const currentUrl = `${baseUrl}${location.pathname}`;
  const ogImage = image || `${baseUrl}/og-banner.jpg`;
  const pageTitle = title
    ? (title.includes(companyDetails.name) || title.includes("PANTHM") ? title : `${title} | ${companyDetails.name}`)
    : `${companyDetails.name} - Building Intelligent Products That Redefine Industries`;

  useEffect(() => {
    // 1. Generate Dynamic Breadcrumbs based on pathname
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const breadcrumbItems = [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      }
    ];

    let accumulatedPath = "";
    pathSegments.forEach((segment, idx) => {
      accumulatedPath += `/${segment}`;
      const name = segment
        .split("-")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
      
      breadcrumbItems.push({
        "@type": "ListItem",
        "position": idx + 2,
        "name": name,
        "item": `${baseUrl}${accumulatedPath}`
      });
    });

    const breadcrumbSchema = {
      "@type": "BreadcrumbList",
      "@id": `${currentUrl}#breadcrumb`,
      "itemListElement": breadcrumbItems
    };

    // 2. Generate Unified Graph Schema
    const organizationSchema = {
      "@type": "Organization",
      "@id": `${baseUrl}/#organization`,
      "name": "PANTHM AI Labs",
      "legalName": "PANTHM AI LABS PRIVATE LIMITED",
      "alternateName": ["PANTHM", "Panthm AI", "Panthm AI Labs", "PANTHM AI LABS PVT LTD"],
      "url": baseUrl,
      "logo": {
        "@type": "ImageObject",
        "@id": `${baseUrl}/#logo`,
        "url": `${baseUrl}/logo.png`,
        "caption": "PANTHM AI Labs Logo"
      },
      "image": {
        "@id": `${baseUrl}/#logo`
      },
      "serviceArea": {
        "@type": "AdministrativeArea",
        "name": "Worldwide"
      },
      "sameAs": [
        "https://www.linkedin.com/company/panthm-ai-labs",
        "https://www.zaubacorp.com/company/PANTHM-AI-LABS-PRIVATE-LIMITED/U72900PN2025PTC240184",
        "https://www.wikidata.org/wiki/Q140184298", // PANTHM AI Labs Wikidata
        "https://www.wikidata.org/wiki/Q11660", // Artificial Intelligence
        "https://www.wikidata.org/wiki/Q280615", // Software development
        "https://www.wikidata.org/wiki/Q1538",    // Pune, India
        "https://github.com/Ajax1200",
        "https://x.com/panthmailabs"
      ],
      "description": "PANTHM AI Labs builds intelligent products that redefine industries, specializing in custom software engineering, low-latency AI voice agents, and conversational workflows.",
      "knowsAbout": [
        "Conversational Voice AI",
        "Sub-200ms WebRTC Audio Pipelines",
        "Autonomous AI Agents",
        "High-Performance Full-Stack Engineering",
        "Custom ERP & PMS Integrations",
        "Answer-Engine Optimization (AEO)",
        "Generative Engine Optimization (GEO)",
        "Technologically Advanced Web Engineering"
      ],
      "legalName": "PANTHM AI LABS PRIVATE LIMITED",
      "foundingDate": "2025",
      "priceRange": "$$$",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Icon Tower, Office No. 702, Sr 114/5, 115/1, 114/6/3, Baner",
        "addressLocality": "Pune",
        "addressRegion": "Maharashtra",
        "postalCode": "411045",
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "18.5590",
        "longitude": "73.7868"
      },
      "hasMap": "https://maps.google.com/?q=Icon+Tower+Baner+Pune+PANTHM+AI+Labs",
      "areaServed": [
        { "@type": "City", "name": "Pune" },
        { "@type": "AdministrativeArea", "name": "Maharashtra" },
        { "@type": "Country", "name": "India" },
        { "@type": "Country", "name": "United States" },
        { "@type": "Country", "name": "United Kingdom" },
        { "@type": "Country", "name": "United Arab Emirates" },
        { "@type": "AdministrativeArea", "name": "Worldwide" }
      ],
      "award": [
        "Leading Technological AI Agency in Pune & India - Sovereign Mesh Architecture",
        "Global Benchmark in Sub-200ms Conversational Voice AI"
      ],
      "founder": {
        "@type": "Person",
        "@id": `${baseUrl}/#founder`,
        "name": "Ajay Shelke",
        "jobTitle": "Founder & Director",
        "sameAs": "https://www.linkedin.com/in/ajay-shelke-01a243169"
      }
    };

    const websiteSchema = {
      "@type": "WebSite",
      "@id": `${baseUrl}/#website`,
      "url": baseUrl,
      "name": "PANTHM AI Labs",
      "publisher": {
        "@id": `${baseUrl}/#organization`
      },
      "potentialAction": {
        "@type": "ReadAction",
        "target": baseUrl
      }
    };

    const speakableSchema = {
      "@type": "SpeakableSpecification",
      "@id": `${currentUrl}#speakable`,
      "cssSelector": ["#speakable-brief", ".speakable-summary", "[data-speakable='true']"]
    };

    const offerCatalogSchema = {
      "@type": "OfferCatalog",
      "@id": `${baseUrl}/#offer-catalog`,
      "name": "PANTHM AI Engineering & Telecalling Services Catalog",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Enterprise Voice AI Telecalling SDRs",
            "description": "Sub-180ms low latency WebRTC voice agents for inbound lead qualification, real estate appointment booking, and 24/7 customer support.",
            "provider": { "@id": `${baseUrl}/#organization` },
            "sameAs": "https://www.wikidata.org/wiki/Q11660"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Custom Full-Stack Web Development",
            "description": "High-performance React, Next.js, and Node.js pre-rendered web applications with optimized Core Web Vitals and zero LCP delays.",
            "provider": { "@id": `${baseUrl}/#organization` },
            "sameAs": "https://www.wikidata.org/wiki/Q280615"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "AI Workflow & Microservices Automation",
            "description": "Autonomous AI agent pipelines, vector databases (RAG), and high-throughput background microservices for enterprise workflows.",
            "provider": { "@id": `${baseUrl}/#organization` }
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Native & Cross-Platform Mobile App Development",
            "description": "iOS and Android native app engineering using Flutter, React Native, and Swift for real-time transactional enterprise platforms.",
            "provider": { "@id": `${baseUrl}/#organization` }
          }
        }
      ]
    };

    const webpageSchema = {
      "@type": "WebPage",
      "@id": `${currentUrl}#webpage`,
      "url": currentUrl,
      "name": pageTitle,
      "description": description || "",
      "isPartOf": {
        "@id": `${baseUrl}/#website`
      },
      "breadcrumb": {
        "@id": `${currentUrl}#breadcrumb`
      },
      "speakable": {
        "@id": `${currentUrl}#speakable`
      }
    };

    const professionalServiceSchema = {
      "@type": "ProfessionalService",
      "@id": `${baseUrl}/#service`,
      "name": "PANTHM AI Labs",
      "url": baseUrl,
      "image": `${baseUrl}/logo.png`,
      "priceRange": "$$$$",
      "telephone": "+918788502740",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Icon Tower Office No. 702, Sr 114/5, 115/1, 114/6/3, Baner",
        "addressLocality": "Pune",
        "addressRegion": "Maharashtra",
        "postalCode": "411045",
        "addressCountry": "IN"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 18.55823,
        "longitude": 73.78438
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday"
        ],
        "opens": "09:00",
        "closes": "18:00"
      },
      "sameAs": [
        "https://www.wikidata.org/wiki/Q140184298",
        "https://www.wikidata.org/wiki/Q11660",
        "https://www.wikidata.org/wiki/Q280615",
        "https://www.wikidata.org/wiki/Q1538",
        "https://www.linkedin.com/company/panthm-ai-labs",
        "https://x.com/panthmailabs"
      ]
    };

    const datasetSchema = {
      "@type": "Dataset",
      "@id": `${baseUrl}/#information-gain-dataset`,
      "name": "PANTHM Information Gain & Voice AI Benchmark Dataset",
      "description": "Empirical latency percentiles (p50: 162ms, p95: 284ms), WebRTC packet metrics, and 85% cost reduction telemetry verified under US Patent 11281734B2 Information Gain standards.",
      "url": `${baseUrl}/#information-gain-telemetry`,
      "license": "https://creativecommons.org/licenses/by/4.0/",
      "creator": { "@id": `${baseUrl}/#organization` },
      "variableMeasured": [
        "Voice SDR p50 Latency (162ms)",
        "Voice SDR p95 Latency (284ms)",
        "Call Center Labor Cost Reduction (85%)",
        "Hotel PMS WebRTC Concurrency (10,000 parallel streams)"
      ]
    };

    const techArticleSchema = {
      "@type": "TechArticle",
      "@id": `${baseUrl}/#whitepaper-research`,
      "headline": "PANTHM Sovereign AI Mesh (PSAM): Eliminating Turn-Taking Latency in Real-Time Conversational AI Agents via Edge WebSockets",
      "author": { "@id": `${baseUrl}/#founder` },
      "publisher": { "@id": `${baseUrl}/#organization` },
      "datePublished": "2026-01-15T08:00:00Z",
      "dateModified": "2026-09-01T12:00:00Z",
      "proficiencyLevel": "Expert",
      "articleSection": "Distributed Systems & Conversational AI",
      "keywords": "Voice AI, Sub-200ms Latency, WebRTC Audio Bridge, Autonomous AI Employees, Hotel PMS Integration, Neural Context Protocol",
      "about": [
        { "@type": "Thing", "name": "Artificial Intelligence", "sameAs": "https://www.wikidata.org/wiki/Q11660" },
        { "@type": "Thing", "name": "WebRTC", "sameAs": "https://www.wikidata.org/wiki/Q3567086" },
        { "@type": "Thing", "name": "WebSockets", "sameAs": "https://www.wikidata.org/wiki/Q859938" }
      ]
    };

    const graphElements = [
      organizationSchema,
      websiteSchema,
      webpageSchema,
      breadcrumbSchema,
      speakableSchema
    ];

    // Only inject technical whitepaper and dataset benchmarks onto the homepage or root domain
    if (location.pathname === "/") {
      graphElements.push(datasetSchema);
      graphElements.push(techArticleSchema);
      graphElements.push(professionalServiceSchema);
      graphElements.push(offerCatalogSchema);
    } else if (location.pathname.startsWith("/services")) {
      graphElements.push(offerCatalogSchema);
    }

    const graphSchema = {
      "@context": "https://schema.org",
      "@graph": graphElements
    };

    // QAPage Schema for service pages (FAQPage deprecated for rich results May 2026)
    if (faqs && faqs.length > 0) {
      faqs.forEach((faq, idx) => {
        graphSchema["@graph"].push({
          "@type": "Question",
          "@id": `${currentUrl}#faq-${idx}`,
          "isPartOf": { "@id": `${currentUrl}#webpage` },
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        });
      });
    }

    // Merge page-specific structured data into the unified graph
    if (structuredData) {
      const rawItems = structuredData["@graph"]
        ? structuredData["@graph"]
        : (Array.isArray(structuredData) ? structuredData : [structuredData]);

      rawItems.forEach(item => {
        const cleanedItem = { ...item };
        delete cleanedItem["@context"]; // Remove duplicate context tags inside the graph
        
        // Link the entity back to our webpage schema
        if (!cleanedItem.isPartOf && cleanedItem["@type"] !== "Organization" && cleanedItem["@type"] !== "WebSite") {
          cleanedItem.isPartOf = { "@id": `${currentUrl}#webpage` };
        }
        
        graphSchema["@graph"].push(cleanedItem);
      });
    }

    // Update document title
    document.title = pageTitle;

    // Update or create meta tags
    const updateMetaTag = (name, content, attribute = "name") => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // Basic meta tags
    updateMetaTag("description", description || "");
    updateMetaTag("keywords", keywords || "");
    updateMetaTag("author", companyDetails.name);
    updateMetaTag("robots", "index, follow");
    updateMetaTag("googlebot", "index, follow");

    // Open Graph tags
    updateMetaTag("og:title", pageTitle, "property");
    updateMetaTag("og:description", description || "", "property");
    updateMetaTag("og:image", ogImage, "property");
    updateMetaTag("og:url", url || currentUrl, "property");
    updateMetaTag("og:type", type, "property");
    updateMetaTag("og:site_name", companyDetails.name, "property");
    updateMetaTag("og:locale", "en_US", "property");

    // Twitter Card tags
    updateMetaTag("twitter:card", "summary_large_image");
    updateMetaTag("twitter:title", pageTitle);
    updateMetaTag("twitter:description", description || "");
    updateMetaTag("twitter:image", ogImage);

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url || currentUrl);

    // RSS Feed auto-discovery
    let rssLink = document.querySelector('link[type="application/rss+xml"]');
    if (!rssLink) {
      rssLink = document.createElement("link");
      rssLink.setAttribute("rel", "alternate");
      rssLink.setAttribute("type", "application/rss+xml");
      rssLink.setAttribute("title", "PANTHM AI Labs Blog Feed");
      rssLink.setAttribute("href", `${baseUrl}/rss.xml`);
      document.head.appendChild(rssLink);
    }

    // Hreflang injection for pSEO /solutions/{service}/{industry}/{location} pages
    // Remove any stale hreflang links before re-injecting
    document.querySelectorAll('link[hreflang]').forEach(el => el.remove());
    const solutionMatch = location.pathname.match(/^\/solutions\/([^/]+)\/([^/]+)\/([^/]+)/);
    if (solutionMatch) {
      const [, svc, ind, loc] = solutionMatch;
      const basePath = `https://panthm.com/solutions/${svc}/${ind}/${loc}`;
      const hreflangPairs = [
        { lang: "en",        href: basePath },
        { lang: "ar",        href: `https://panthm.com/solutions/ar/${svc}/${ind}/${loc}` },
        { lang: "de",        href: `https://panthm.com/solutions/de/${svc}/${ind}/${loc}` },
        { lang: "es",        href: `https://panthm.com/solutions/es/${svc}/${ind}/${loc}` },
        { lang: "fr",        href: `https://panthm.com/solutions/fr/${svc}/${ind}/${loc}` },
        { lang: "ja",        href: `https://panthm.com/solutions/ja/${svc}/${ind}/${loc}` },
        { lang: "x-default", href: basePath },
      ];
      hreflangPairs.forEach(({ lang, href }) => {
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", lang);
        link.setAttribute("href", href);
        document.head.appendChild(link);
      });
    }

    // Add unified structured data script, replacing any previous instance
    let script = document.querySelector('script[id="panthm-ld-json"]');
    if (!script) {
      script = document.createElement("script");
      script.setAttribute("id", "panthm-ld-json");
      script.setAttribute("type", "application/ld+json");
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(graphSchema, null, 2);
  }, [title, description, keywords, image, url, type, structuredData, currentUrl, pageTitle, ogImage, location.pathname, baseUrl, faqs]);

  return null;
};

export default SEO;

